import type { ParticleLayout } from "./particlePhotoGeometry";

interface WorkerResult {
  id: number;
  data: Float32Array | null;
  width: number;
  height: number;
}

export interface LocalModelDepthField {
  data: Float32Array;
  width: number;
  height: number;
}

export type LocalModelDepthSource = "cache" | "model";

export type ParticleDepthResolution = "pending" | "model" | "unavailable";
export type ParticleDepthEntryMode = "static-photo" | "v2-particles" | "fallback-particles";

export interface ResolvedLocalModelDepthField {
  field: LocalModelDepthField;
  source: LocalModelDepthSource;
}

interface CachedDepthField {
  key: string;
  version: 1;
  width: number;
  height: number;
  data: ArrayBuffer;
  cachedAt: number;
}

const MODEL_DEPTH_RANGE = 0.32;
const DEPTH_CACHE_DATABASE = "memory-universe-derived-assets";
const DEPTH_CACHE_STORE = "depth-anything-v2";
const DEPTH_CACHE_VERSION = 1;
const DEPTH_CACHE_MAX_ENTRIES = 24;
const DEPTH_RELAY_MAGIC = "MUD2";
const DEPTH_RELAY_VERSION = 1;
const DEPTH_RELAY_MAX_SIDE = 256;
const DEPTH_RELAY_HEADER_BYTES = 12;

let worker: Worker | null = null;
let requestId = 0;
const pending = new Map<number, (result: WorkerResult | null) => void>();
let depthCacheDatabase: Promise<IDBDatabase | null> | null = null;

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(maximum, Math.max(minimum, value));
}

function requestResult<T>(request: IDBRequest<T>) {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("无法读取本地深度缓存"));
  });
}

function transactionDone(transaction: IDBTransaction) {
  return new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("无法保存本地深度缓存"));
    transaction.onabort = () => reject(transaction.error ?? new Error("本地深度缓存操作已取消"));
  });
}

function openDepthCache() {
  if (typeof indexedDB === "undefined") return Promise.resolve<IDBDatabase | null>(null);
  depthCacheDatabase ??= new Promise<IDBDatabase | null>((resolve) => {
    const request = indexedDB.open(DEPTH_CACHE_DATABASE, DEPTH_CACHE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(DEPTH_CACHE_STORE)) {
        database.createObjectStore(DEPTH_CACHE_STORE, { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
  return depthCacheDatabase;
}

function cachedFieldToLocalField(cached: CachedDepthField | undefined) {
  if (
    !cached ||
    cached.version !== 1 ||
    !Number.isInteger(cached.width) ||
    !Number.isInteger(cached.height) ||
    cached.width < 1 ||
    cached.height < 1 ||
    !(cached.data instanceof ArrayBuffer) ||
    cached.data.byteLength !== cached.width * cached.height * Float32Array.BYTES_PER_ELEMENT
  ) {
    return null;
  }
  return {
    data: new Float32Array(cached.data.slice(0)),
    width: cached.width,
    height: cached.height
  } satisfies LocalModelDepthField;
}

async function readCachedLocalModelDepthField(cacheKey: string) {
  try {
    const database = await openDepthCache();
    if (!database) return null;
    const transaction = database.transaction(DEPTH_CACHE_STORE, "readonly");
    const done = transactionDone(transaction);
    const cached = await requestResult(
      transaction.objectStore(DEPTH_CACHE_STORE).get(cacheKey) as IDBRequest<CachedDepthField | undefined>
    );
    await done;
    return cachedFieldToLocalField(cached);
  } catch {
    return null;
  }
}

export async function readCachedV2DepthField(cacheKey: string | undefined) {
  const safeCacheKey = cacheKey?.trim().slice(0, 256);
  return safeCacheKey ? readCachedLocalModelDepthField(safeCacheKey) : null;
}

async function cacheLocalModelDepthField(cacheKey: string, field: LocalModelDepthField) {
  try {
    const database = await openDepthCache();
    if (!database) return;
    const transaction = database.transaction(DEPTH_CACHE_STORE, "readwrite");
    const done = transactionDone(transaction);
    const store = transaction.objectStore(DEPTH_CACHE_STORE);
    store.put({
      key: cacheKey,
      version: 1,
      width: field.width,
      height: field.height,
      data: field.data.slice().buffer,
      cachedAt: Date.now()
    } satisfies CachedDepthField);
    const entries = await requestResult(store.getAll() as IDBRequest<CachedDepthField[]>);
    const obsolete = entries
      .filter((entry) => entry.key !== cacheKey)
      .toSorted((left, right) => left.cachedAt - right.cachedAt)
      .slice(0, Math.max(0, entries.length - DEPTH_CACHE_MAX_ENTRIES));
    for (const entry of obsolete) {
      store.delete(entry.key);
    }
    await done;
  } catch {
    // Derived depth can always be regenerated, so cache failures remain non-fatal.
  }
}

function resampleDepthField(field: LocalModelDepthField, maximumSide = DEPTH_RELAY_MAX_SIDE): LocalModelDepthField {
  const longest = Math.max(field.width, field.height);
  if (longest <= maximumSide && field.width >= 16 && field.height >= 16) {
    return { data: field.data.slice(), width: field.width, height: field.height };
  }
  const scale = longest > maximumSide ? maximumSide / longest : 1;
  const width = Math.min(maximumSide, Math.max(16, Math.round(field.width * scale)));
  const height = Math.min(maximumSide, Math.max(16, Math.round(field.height * scale)));
  const data = new Float32Array(width * height);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      data[y * width + x] = sampleDepth(
        field.data,
        field.width,
        field.height,
        (x / Math.max(1, width - 1)) * (field.width - 1),
        (y / Math.max(1, height - 1)) * (field.height - 1)
      );
    }
  }
  return { data, width, height };
}

export function encodeV2DepthField(field: LocalModelDepthField) {
  const sampled = resampleDepthField(field);
  const bytes = new Uint8Array(DEPTH_RELAY_HEADER_BYTES + sampled.data.length * Uint16Array.BYTES_PER_ELEMENT);
  const view = new DataView(bytes.buffer);
  for (let index = 0; index < DEPTH_RELAY_MAGIC.length; index += 1) bytes[index] = DEPTH_RELAY_MAGIC.charCodeAt(index);
  view.setUint8(4, DEPTH_RELAY_VERSION);
  view.setUint16(6, sampled.width, false);
  view.setUint16(8, sampled.height, false);
  for (let index = 0; index < sampled.data.length; index += 1) {
    const normalized = clamp((sampled.data[index]! + MODEL_DEPTH_RANGE) / (MODEL_DEPTH_RANGE * 2), 0, 1);
    view.setUint16(DEPTH_RELAY_HEADER_BYTES + index * 2, Math.round(normalized * 65535), false);
  }
  return { bytes, width: sampled.width, height: sampled.height };
}

export function decodeV2DepthField(bytes: Uint8Array): LocalModelDepthField | null {
  if (bytes.byteLength < DEPTH_RELAY_HEADER_BYTES) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  for (let index = 0; index < DEPTH_RELAY_MAGIC.length; index += 1) {
    if (view.getUint8(index) !== DEPTH_RELAY_MAGIC.charCodeAt(index)) return null;
  }
  const version = view.getUint8(4);
  const width = view.getUint16(6, false);
  const height = view.getUint16(8, false);
  if (version !== DEPTH_RELAY_VERSION || width < 16 || height < 16 || width > DEPTH_RELAY_MAX_SIDE || height > DEPTH_RELAY_MAX_SIDE
    || bytes.byteLength !== DEPTH_RELAY_HEADER_BYTES + width * height * 2) return null;
  const data = new Float32Array(width * height);
  for (let index = 0; index < data.length; index += 1) {
    data[index] = (view.getUint16(DEPTH_RELAY_HEADER_BYTES + index * 2, false) / 65535) * MODEL_DEPTH_RANGE * 2 - MODEL_DEPTH_RANGE;
  }
  return { data, width, height };
}

export async function cacheReceivedV2DepthField(cacheKey: string, field: LocalModelDepthField) {
  const safeCacheKey = cacheKey.trim().slice(0, 256);
  if (safeCacheKey) await cacheLocalModelDepthField(safeCacheKey, field);
}

export function particleDepthEntryMode(resolution: ParticleDepthResolution): ParticleDepthEntryMode {
  // V2 is an enhancement to the spatial depth, not a gate for the particle
  // scene. Keep the image particleized while an authorized device computes or
  // receives the derived depth field, then replace only its depth data.
  return resolution === "model" ? "v2-particles" : "fallback-particles";
}

function getWorker() {
  if (!worker) {
    worker = new Worker(new URL("./particleDepth.worker.ts", import.meta.url), { type: "module" });
    worker.onmessage = (event: MessageEvent<WorkerResult>) => {
      const resolve = pending.get(event.data.id);
      pending.delete(event.data.id);
      resolve?.(event.data);
    };
    worker.onerror = () => {
      for (const resolve of pending.values()) {
        resolve(null);
      }
      pending.clear();
      worker?.terminate();
      worker = null;
    };
  }
  return worker;
}

async function inferDepth(image: CanvasImageSource, width: number, height: number) {
  try {
    const bitmap = await createImageBitmap(image as ImageBitmapSource);
    const id = ++requestId;
    const result = new Promise<WorkerResult | null>((resolve) => pending.set(id, resolve));
    getWorker().postMessage({ id, bitmap, width, height }, [bitmap]);
    return result;
  } catch {
    return null;
  }
}

function sampleDepth(
  data: Float32Array,
  width: number,
  height: number,
  x: number,
  y: number
) {
  const left = clamp(Math.floor(x), 0, width - 1);
  const right = clamp(left + 1, 0, width - 1);
  const top = clamp(Math.floor(y), 0, height - 1);
  const bottom = clamp(top + 1, 0, height - 1);
  const horizontal = x - Math.floor(x);
  const vertical = y - Math.floor(y);
  const upper = data[top * width + left]! * (1 - horizontal) + data[top * width + right]! * horizontal;
  const lower = data[bottom * width + left]! * (1 - horizontal) + data[bottom * width + right]! * horizontal;
  return upper * (1 - vertical) + lower * vertical;
}

export function sampleLocalModelDepths(field: LocalModelDepthField, layout: ParticleLayout) {
  const depths = new Float32Array(layout.count);
  for (let index = 0; index < layout.count; index += 1) {
    const uvIndex = index * 2;
    depths[index] = sampleDepth(
      field.data,
      field.width,
      field.height,
      layout.photoUvs[uvIndex]! * (field.width - 1),
      (1 - layout.photoUvs[uvIndex + 1]!) * (field.height - 1)
    );
  }
  return depths;
}

export function normalizeModelDepths(data: Float32Array) {
  const finite = Array.from(data).filter(Number.isFinite).sort((left, right) => left - right);
  if (finite.length < 2) return null;

  // Depth Anything produces relative depth. Robust percentiles keep a few
  // extreme pixels from stretching the whole photograph into a deep wall.
  const lower = finite[Math.floor((finite.length - 1) * 0.03)]!;
  const upper = finite[Math.ceil((finite.length - 1) * 0.97)]!;
  if (upper - lower < 0.00001) return null;

  const normalizedDepths = new Float32Array(data.length);
  for (let index = 0; index < normalizedDepths.length; index += 1) {
    const value = Number.isFinite(data[index]) ? data[index]! : (lower + upper) / 2;
    const normalized = clamp((value - lower) / (upper - lower), 0, 1);
    const continuous = normalized * normalized * (3 - 2 * normalized);
    normalizedDepths[index] = (continuous * 2 - 1) * MODEL_DEPTH_RANGE;
  }
  return normalizedDepths;
}

export async function createLocalModelDepthField(image: CanvasImageSource, aspectRatio: number) {
  const longestSide = 512;
  const safeAspectRatio = clamp(aspectRatio, 0.01, 100);
  const inputWidth = safeAspectRatio >= 1 ? longestSide : Math.max(1, Math.round(longestSide * safeAspectRatio));
  const inputHeight = safeAspectRatio >= 1 ? Math.max(1, Math.round(longestSide / safeAspectRatio)) : longestSide;
  try {
    const result = await inferDepth(image, inputWidth, inputHeight);
    if (!result?.data || !result.width || !result.height) {
      return null;
    }
    const { data, width, height } = result;
    if (data.length < width * height) {
      return null;
    }

    const normalizedDepths = normalizeModelDepths(data);
    if (!normalizedDepths) return null;
    return { data: normalizedDepths, width, height } satisfies LocalModelDepthField;
  } catch {
    // The heuristic depth field remains the offline and low-power fallback.
    return null;
  }
}

export async function resolveLocalModelDepthField(
  cacheKey: string | undefined,
  image: CanvasImageSource,
  aspectRatio: number
): Promise<ResolvedLocalModelDepthField | null> {
  const safeCacheKey = cacheKey?.trim().slice(0, 256);
  if (safeCacheKey) {
    const cached = await readCachedLocalModelDepthField(safeCacheKey);
    if (cached) return { field: cached, source: "cache" };
  }

  const field = await createLocalModelDepthField(image, aspectRatio);
  if (!field) return null;
  if (safeCacheKey) void cacheLocalModelDepthField(safeCacheKey, field);
  return { field, source: "model" };
}

export async function createLocalModelDepths(image: CanvasImageSource, layout: ParticleLayout) {
  const field = await createLocalModelDepthField(image, layout.columns / layout.rows);
  return field ? sampleLocalModelDepths(field, layout) : null;
}
